import { useCallback, useMemo, useState } from "react";
import { CreateTitration, InputType } from "src/api/types";

type FormValues = Omit<
  CreateTitration,
  "duration_in_months" | "product_id" | "product_category_id"
>;

export const useInputs = () => {
  const [values, setValues] = useState<FormValues>({
    dose_unit: "",
    tier: 0,
    total_dose: 0,
    min_dose: null,
    max_dose: null,
    slug: "",
  });

  const setFields = useCallback(
    <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const inputs: InputType<CreateTitration>[] = useMemo(
    () => [
      {
        type: "number",
        name: "tier",
        label: "Tier",
        required: true,
        value: values.tier,
        onChange: (v) => setFields("tier", v ?? 0),
      },
      {
        type: "number",
        name: "total_dose",
        label: "Total dose",
        required: true,
        value: values.total_dose,
        onChange: (v) => setFields("total_dose", v ?? 0),
      },
      {
        type: "number",
        name: "min_dose",
        label: "Min dose",
        required: false,
        value: values.min_dose ?? null,
        onChange: (v) => setFields("min_dose", v ?? null),
      },
      {
        type: "number",
        name: "max_dose",
        label: "Max dose",
        required: false,
        value: values.max_dose || null,
        onChange: (v) => setFields("max_dose", v ?? null),
      },
      {
        type: "text",
        name: "dose_unit",
        label: "Dose unit",
        required: true,
        value: values.dose_unit,
        onChange: (v) => setFields("dose_unit", v),
      },
      {
        type: "text",
        name: "slug",
        label: "Slug",
        required: false,
        value: values.slug ?? "",
        onChange: (v) => setFields("slug", v ?? ""),
      },
    ],
    [
      setFields,
      values.dose_unit,
      values.max_dose,
      values.min_dose,
      values.slug,
      values.tier,
      values.total_dose,
    ],
  );

  return { values, inputs, setValues };
};
